import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import {
  So5LeaderboardType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { Ambassadors } from '@sorare/core/src/components/landing/Ambassadors';
import { CommunityBlock } from '@sorare/core/src/components/landing/CommunityBlock';
import { Compete } from '@sorare/core/src/components/landing/Compete';
import type { CompeteAssets } from '@sorare/core/src/components/landing/Compete';
import { Footer } from '@sorare/core/src/components/landing/Footer';
import { HeroVideo } from '@sorare/core/src/components/landing/HeroVideo';
import { Leagues } from '@sorare/core/src/components/landing/Leagues';
import { Scout } from '@sorare/core/src/components/landing/Scout';
import type { ScoutAssets } from '@sorare/core/src/components/landing/Scout';
import { Win } from '@sorare/core/src/components/landing/Win';
import type { WinAssets } from '@sorare/core/src/components/landing/Win';
import { glossary } from '@sorare/core/src/lib/glossary';
import { NBATeams } from '@sorare/core/src/lib/nbaTeams';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { LeagueImage } from '@sorare/gameplay/src/components/league/LeagueImage';

import { FAQ } from '../FAQ';
import footerBackgroundAvif from './assets/footer/background.avif';
import footerBackground from './assets/footer/background.jpg';
import winBackgroundAvif from './assets/win/background.avif';
import winBackground from './assets/win/background.jpg';
import cashAvif from './assets/win/cash.avif';
import cash from './assets/win/cash.png';
import ticketsAvif from './assets/win/tickets.avif';
import tickets from './assets/win/tickets.png';
import vipAvif from './assets/win/vip.avif';
import vip from './assets/win/vip.png';

const leaguesAssets = [
  {
    id: 'common',
    pictureUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/picture_daily_common.png',
    headerUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/header_daily_common.png',
  },
  {
    id: 'limited',
    pictureUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/picture_daily_limited.png',
    headerUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/header_daily_limited.png',
  },
  {
    id: 'rare',
    pictureUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/picture_daily_rare.png',
    headerUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/header_daily_rare.png',
  },
  {
    id: 'super_rare',
    pictureUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/picture_daily_super_rare.png',
    headerUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/header_daily_super_rare.png',
  },
  {
    id: 'unique',
    pictureUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/picture_daily_unique.png',
    headerUrl:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/header_daily_unique.png',
  },
];

const Root = styled.section`
  display: flex;
  isolation: isolate;
  flex-direction: column;
  gap: var(--quadruple-unit);
  overflow: hidden;
  position: relative;
  padding-bottom: var(--double-unit);
  @media ${tabletAndAbove} {
    gap: calc(8 * var(--unit));
  }
`;

const Players = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex: 1;
  background-image: var(--players);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center top -5%;
  min-width: 100%;
  max-width: 100%;
  aspect-ratio: 746/481;
  @media ${laptopAndAbove} {
    height: 480px;
    background-position: right center;
  }
`;

const ImgWrapper = styled.div`
  padding: 0 var(--unit);
`;

type Props = {
  showFAQ?: boolean;
  assets: {
    compete: CompeteAssets & { cards: { avif: string; pictureUrl: string } };
    scout: ScoutAssets;
    win: Omit<WinAssets, 'background'>;
  };
  leaderboards: So5LeaderboardType[];
  header?: ReactNode;
};

export const EarlyAccess = ({
  showFAQ,
  assets,
  leaderboards,
  header,
}: Props) => {
  return (
    <Root>
      <div>
        {header || (
          <HeroVideo
            title={
              <FormattedMessage
                id="nba.landing.EarlyAccess.Hero.title"
                defaultMessage="A new era of Fantasy basketball"
              />
            }
            subtitle={
              <FormattedMessage
                id="nba.landing.EarlyAccess.Hero.description"
                defaultMessage="Build a collection of cards and compete for cash, VIP experiences, and more. Sorare is a free-to-play game where your cards do the talking"
              />
            }
          />
        )}
        <Leagues
          leagues={NBATeams.map(({ fullName, svgUrl }) => ({
            displayName: fullName,
            pictureSecondaryUrl: svgUrl,
          }))}
        />
      </div>
      <Scout
        assets={assets.scout}
        title={
          <FormattedMessage
            id="nba.landing.EarlyAccess.scout.title"
            defaultMessage="Build"
          />
        }
        description={
          <FormattedMessage
            id="nba.landing.EarlyAccess.scout.description"
            defaultMessage="Create your ultimate collection of official NBA digital player cards. Trade, buy, and sell them on the marketplace to keep your edge"
          />
        }
        ctaLabel={<FormattedMessage {...glossary.joinForFree} />}
        players={
          <Players
            style={
              {
                '--players': `image-set(url(${assets.scout.players.avif}) type('image/avif'), url(${assets.scout.players.pictureUrl}) type('image/jpeg'))`,
              } as CSSProperties
            }
          />
        }
      >
        {leaguesAssets.map(({ id, ...assetProps }) => (
          <LeagueImage key={id} {...assetProps} />
        ))}
      </Scout>
      <Compete
        assets={assets.compete}
        title={
          <FormattedMessage
            id="'nba.landing.EarlyAccess.Compete.title"
            defaultMessage="Compete"
          />
        }
        description={
          <FormattedMessage
            id="'nba.landing.EarlyAccess.Compete.description"
            defaultMessage="Set 5 player lineups and compete in daily & bi-weekly fantasy basketball competitions"
          />
        }
        img={
          <ImgWrapper>
            <picture>
              <source srcSet={assets.compete.cards.avif} type="image/avif" />
              <Img
                src={assets.compete.cards.pictureUrl}
                fetchpriority="low"
                width="543"
                height="661"
              />
            </picture>
          </ImgWrapper>
        }
      />
      <Container>
        <Win
          assets={{
            ...assets.win,
            background: {
              avif: winBackgroundAvif,
              pictureUrl: winBackground,
            },
          }}
          title={
            <FormattedMessage
              id="nba.EarlyAccess.Win.title"
              defaultMessage="Become a Legend"
            />
          }
          description={
            <FormattedMessage
              id="nba.EarlyAccess.Win.Legacy"
              defaultMessage="Cement your legacy as the best Manager in the Sorare community. Show off your basketball IQ and build the ultimate collection!"
            />
          }
          ctaLabel={<FormattedMessage {...glossary.joinForFree} />}
          rewards={[
            {
              id: 'vip',
              avif: vipAvif,
              pictureUrl: vip,
              width: 176,
              height: 248,
              label: (
                <FormattedMessage
                  id="nba.landing.EarlyAccess.Win.vip"
                  defaultMessage="Vip Experiences"
                />
              ),
            },
            {
              id: 'cards',
              avif: assets.win.cards.avif,
              pictureUrl: assets.win.cards.pictureUrl,
              width: 135,
              height: 208,
              label: (
                <FormattedMessage
                  id="nba.landing.EarlyAccess.Win.cards"
                  defaultMessage="Playable Cards"
                />
              ),
            },
            {
              id: 'cash',
              avif: cashAvif,
              pictureUrl: cash,
              width: 222,
              height: 245,
              label: (
                <FormattedMessage
                  id="nba.landing.EarlyAccess.Win.cash"
                  defaultMessage="Cash Rewards"
                />
              ),
            },
            {
              id: 'tickets',
              avif: ticketsAvif,
              pictureUrl: tickets,
              width: 224,
              height: 162,
              label: (
                <FormattedMessage
                  id="nba.landing.EarlyAccess.Win.tickets"
                  defaultMessage="Game Tickets"
                />
              ),
            },
          ]}
        />
      </Container>
      <CommunityBlock
        leaderboards={leaderboards.map(leaderboardType => ({
          sport: Sport.NBA,
          leaderboardType,
        }))}
      />
      <Container>
        <Ambassadors />
      </Container>
      <Footer
        title={
          <FormattedMessage
            id="nba.landing.EarlyAccess.Footer.title"
            defaultMessage="Your new basketball home"
          />
        }
        assets={{ avif: footerBackgroundAvif, pictureUrl: footerBackground }}
      />
      {showFAQ && <FAQ />}
    </Root>
  );
};
